import { Component, computed, inject, Inject, linkedSignal, OnDestroy, OnInit, untracked } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    form,
    max,
    maxLength,
    min,
    minLength,
    pattern,
    required,
    submit,
    validate,
    ValidationError,
} from '@angular/forms/signals';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment, { HTML5_FMT } from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { mergeMap, take } from 'rxjs/operators';
import { InitializationProgressComponent } from '../../../components/initialization-progress/initialization-progress.component';
import { DisplayDecorator } from '../../../decorators/display-decorator';
import { InitializationInstanceToken } from '../../../interfaces/initialization.interface';
import { ODataModel, ODataModelChangeEvent } from '../../../interfaces/odata-model.interface';
import { DropdownModel } from '../../../models/complex/dropdown.model';
import { RadioModel } from '../../../models/complex/radio.model';
import { BooleanModelOptions } from '../../../models/options/boolean-model-options';
import { DateModelOptions } from '../../../models/options/date-model-options';
import { DropdownModelOptions } from '../../../models/options/dropdown-model-options';
import { DurationModelOptions } from '../../../models/options/duration-model-options';
import { NumberModelOptions } from '../../../models/options/number-model-options';
import { RadioModelOptions } from '../../../models/options/radio-model-options';
import { TextModelOptions } from '../../../models/options/text-model-options';
import { TimeModelOptions } from '../../../models/options/time-model-options';
import { TreeViewModelOptions } from '../../../models/options/treeview-model-options';
import { BooleanModel } from '../../../models/primitive/boolean.model';
import { DateModel } from '../../../models/primitive/date.model';
import { DurationModel } from '../../../models/primitive/duration.model';
import { NumberModel } from '../../../models/primitive/number.model';
import { TextModel } from '../../../models/primitive/text.model';
import { TimeModel } from '../../../models/primitive/time.model';
import { TreeViewModel } from '../../../models/primitive/tree-view.model';
import { BaseInitializationService } from '../../../services/base-initialization/base-initialization.service';
import { assignModelOptions } from '../../../utilities/assign-model-options';
import { OutmodelComponent } from '../outmodel/outmodel.component';
import { createTreeViewData } from './create-tree-view-data';
import { RatingModel } from '../../../models/primitive/rating.model';
import { RatingModelOptions } from '../../../models/options/rating-model-options';
import { PCHAlertService } from '../../../../../../../apps/pace/src/app/core/services/pch-alerts/pch-alerts.service';
import { getUuidV4 } from '../../../utilities/generic-utilities';
import { NotificationIdentifier } from '../../../types/notification-identifier';
import { forbiddenCharacters } from '../../../signal-form-rules/forbidden-characters.rule';
import { selectEmojisRegex } from '../../../state/config/selectors/config.selector';
import {
    selectWidgetsForm,
    selectWidgetsFormRevision,
    widgetsFormCommitted,
    widgetsFormReset,
    widgetsFormSeeded,
    WidgetsFormState,
} from '../../../state/widgets';
import { SelectFieldOption, TreeFieldNode } from '@evorait/pace-components';
import { ScanFnFactory } from '../../../services/barcode-scanner/scan-fn-factory.service';

/**
 * Moment format the signal-form's range messages render their bounds with. The
 * real timesheet form should take this from `ConfigInterface.getDateTimeFormatPattern()`;
 * the test screen deliberately avoids that dependency so it renders even before
 * the config service has loaded.
 */
const signalFormDateDisplayFormat = 'L';

/**
 * Formats the signal-form's time values are parsed with, strictly — the field
 * emits `HH:mm`, or `HH:mm:ss` once `displaySeconds` is set on it.
 */
const signalFormTimeParseFormats = [HTML5_FMT.TIME, HTML5_FMT.TIME_SECONDS];

/**
 * Constraints of the demo text field, mirroring the reactive-forms text widget
 * above it (length 5-10, `Initial.*`). The pattern is anchored because signal
 * forms' `pattern` validator tests rather than full-matches.
 */
const signalFormTextMinLength = 5;
const signalFormTextMaxLength = 10;
const signalFormTextPattern = /^Initial.*$/;

@Component({
    selector: 'pace-core-widgets',
    templateUrl: './widgets.component.html',
    styleUrls: ['./widgets.component.scss'],
    standalone: false,
})
export class WidgetsComponent implements OnInit, OnDestroy {
    public appReinitializationRunning = false;
    private appReinitSubscription?: Subscription;
    public serviceReinitializationRunning = false;
    private serviceReinitSubscription?: Subscription;

    public widgetForm: FormGroup;
    public formChildren: FormArray;

    public textInputTest: TextModel;
    public textInputScannableTest: TextModel;
    public textInputUnitTest: TextModel;
    public textInputReadonlyTest: TextModel;
    public numberInputTest: NumberModel;
    public numberInputScannableTest: NumberModel;
    public numberStepperTest: NumberModel;
    public durationStepperTest: DurationModel;
    public durationStepperStepTest: DurationModel;
    public durationStepperUndefinedTest: DurationModel;
    public durationStepperMaxValueTest: DurationModel;
    public dateInputTest: DateModel;
    public timeInputTest: TimeModel;
    public dateTimeInputTest: TimeModel;
    public dropdownTest: DropdownModel<TextModel>;
    public dropdownInputTest: DropdownModel<TextModel>;
    public dropdownDisabledTest: DropdownModel<TextModel>;
    public dropdownMultiTest: DropdownModel<TextModel>;
    public radioInputTest: RadioModel<TextModel>;
    public checkboxInputTest: DisplayDecorator<BooleanModel>;
    public checkboxInputToggleTest: DisplayDecorator<BooleanModel>;
    public treeViewTest: TreeViewModel<TextModel>;
    public ratingInputTest: RatingModel;

    public alertTimeInSeconds = 10;

    // --- Signal forms against the widgets store slice ------------------------
    //
    // Separate from `widgetForm` above on purpose: the reactive form drives the
    // legacy `*Model`-based widgets, this one drives the new store-backed fields.
    // Note what is *not* here — no per-field selector, no per-field action, no
    // subscription. The store connection is made once, for the whole form.

    /** inclusive bounds of the demo date field, as HTML5 `yyyy-MM-dd` strings */
    public readonly signalFormMinDate = '2024-01-01';
    public readonly signalFormMaxDate = '2026-12-31';

    /** inclusive bounds of the demo time field, as HTML5 `HH:mm` strings */
    public readonly signalFormMinTime = '06:00';
    public readonly signalFormMaxTime = '22:00';

    /** constraints of the demo multiline text field */
    public readonly signalFormMultilineTextMaxLength = 200;
    public readonly signalFormMultilineTextMaxLines = 3;

    /** inclusive bounds of the demo number field */
    public readonly signalFormMinNumber = 0;
    public readonly signalFormMaxNumber = 1000;

    /** bounds and increment of the demo number stepper, mirroring the reactive-forms one */
    public readonly signalFormStepperStep = 5;
    public readonly signalFormMinStepper = 0;
    public readonly signalFormMaxStepper = 1000;

    /**
     * Bounds and increment of the demo duration stepper, all in minutes — mirroring the
     * reactive-forms `durationStepperMaxValueTest` (00:05 to 01:30, step 5).
     */
    public readonly signalFormDurationStep = 5;
    public readonly signalFormMinDuration = 5;
    public readonly signalFormMaxDuration = 90;

    /** the demo dropdown is multi-select, mirroring the reactive-forms `dropdownMultiTest` */
    public readonly signalFormMaxSelections = 3;

    /** stars offered by the demo rating, mirroring the reactive-forms `ratingInputTest` */
    public readonly signalFormMaxRating = 10;

    /** the demo tree allows two leaves, mirroring the reactive-forms `treeViewTest` */
    public readonly signalFormMaxTreeSelections = 2;

    /**
     * Nodes of the demo tree. Unlike `createTreeViewData` this needs no invented root and
     * no parent fix-up pass — the field takes the top-level nodes and only walks downwards.
     */
    public readonly signalFormTreeNodes: TreeFieldNode[] = [
        { value: 'child-2', label: 'Child 2' },
        { value: 'child-3', label: 'Child 3' },
        {
            value: 'child-4',
            label: 'Child 4',
            children: [
                { value: 'child-5', label: 'Child 5' },
                {
                    value: 'child-6',
                    label: 'Child 6',
                    children: [
                        { value: 'child-8', label: 'Child 8' },
                        { value: 'child-9', label: 'Child 9' },
                    ],
                },
                { value: 'child-7', label: 'Child 7', disabled: true },
            ],
        },
    ];

    /** choices offered by the demo dropdown, in the plain shape the field expects */
    public readonly signalFormDropdownOptions: SelectFieldOption[] = [
        { value: 'first', label: 'First entry' },
        { value: 'second', label: 'Second entry' },
        { value: 'third', label: 'Third entry' },
        { value: 'fourth', label: 'Fourth entry' },
        { value: 'retired', label: 'Retired entry', disabled: true },
    ];

    /** choices offered by the demo radio group, mirroring the reactive-forms `radioInputTest` */
    public readonly signalFormRadioOptions: SelectFieldOption[] = [
        { value: 'first', label: 'First' },
        { value: 'second', label: 'Second' },
        { value: 'unavailable', label: 'Unavailable', disabled: true },
    ];

    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly scanFnFactory = inject(ScanFnFactory);

    /**
     * Forbidden-character pattern, read straight from the config slice. The selector
     * falls back to `ConfigConstants.defaultEmojiRegex` while the slice is still
     * empty, so the rule stays active before config has loaded.
     */
    private readonly emojisRegex = this.store.selectSignal(selectEmojisRegex);

    /** resolved lazily so the schema does not translate before the language files are in */
    private readonly checkboxRequiredMessage = (): string => <string>this.translateService.instant('ERROR_REQUIRED');

    /** the committed values, rendered next to the field so the round trip is visible */
    public readonly committedWidgetsForm = this.store.selectSignal(selectWidgetsForm);

    private readonly committedWidgetsFormRevision = this.store.selectSignal(selectWidgetsFormRevision);

    /**
     * The in-flight copy of the committed values that the signal form edits.
     *
     * It re-seeds from the store only when the slice's `revision` changes — i.e.
     * on a committed write — so a store update that is not a commit cannot
     * discard what the user is currently typing. `untracked` is what keeps the
     * committed read out of the linked signal's dependencies; without it the
     * plain reactivity of the computation would re-seed on any `form` change.
     */
    public readonly signalFormDraft = linkedSignal<number, WidgetsFormState>({
        source: this.committedWidgetsFormRevision,
        computation: () => untracked(() => ({ ...this.committedWidgetsForm() })),
    });

    /**
     * Validation lives here, with the form — not in the field component (which
     * stays reusable) and not in the reducer (which only applies committed
     * values). Validators run in a reactive context, so rules that depend on
     * config or value-help signals can read them directly.
     */
    public readonly signalForm = form(this.signalFormDraft, (path) => {
        required(path.dateValue, {
            message: <string>this.translateService.instant('DATE_INPUT_SELECT_DATE_RANGE_TEXT'),
        });
        validate(path.dateValue, (context) => this.checkSignalFormDateRange(context.value()));

        required(path.timeValue, {
            message: <string>this.translateService.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT'),
        });
        validate(path.timeValue, (context) => this.checkSignalFormTimeRange(context.value()));

        // The text field needs no hand-written rules at all: signal forms ship the
        // length and pattern validators, and they reach the native input through
        // the `FormUiControl` contract without any binding in the template.
        required(path.textValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        minLength(path.textValue, signalFormTextMinLength, {
            message: <string>this.translateService.instant('ERROR_MINLENGTH_NOT_REACHED'),
        });
        maxLength(path.textValue, signalFormTextMaxLength, {
            message: <string>this.translateService.instant('ERROR_MAXLENGTH_EXCEEDED'),
        });
        pattern(path.textValue, signalFormTextPattern, {
            message: <string>this.translateService.instant('ERROR_PATTERN_NOT_MATCHED'),
        });
        forbiddenCharacters(path.textValue, this.emojisRegex, {
            message: (characters) => <string>this.translateService.instant('ERROR_FORBIDDEN_CHARACTERS', {
                    forbiddenCharacters: characters,
                }),
        });

        required(path.multilineTextValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        maxLength(path.multilineTextValue, this.signalFormMultilineTextMaxLength, {
            message: <string>this.translateService.instant('ERROR_MAXLENGTH_EXCEEDED'),
        });
        validate(path.multilineTextValue, (context) => this.checkSignalFormLineCount(context.value()));
        forbiddenCharacters(path.multilineTextValue, this.emojisRegex, {
            message: (characters) => <string>this.translateService.instant('ERROR_FORBIDDEN_CHARACTERS', {
                    forbiddenCharacters: characters,
                }),
        });

        // `min`/`max` reach the native input through the `FormUiControl` contract, so
        // the number field needs no bounds inputs of its own
        required(path.numberValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        min(path.numberValue, this.signalFormMinNumber, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });
        max(path.numberValue, this.signalFormMaxNumber, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });

        required(path.numberStepperValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        min(path.numberStepperValue, this.signalFormMinStepper, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });
        max(path.numberStepperValue, this.signalFormMaxStepper, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });

        // `required` on a boolean is ambiguous — `false` is a perfectly good value, not
        // a missing one — so "must be ticked" is spelled out instead of inferred
        validate(path.checkboxValue, (context) =>
            context.value() ? undefined : { kind: 'required', message: this.checkboxRequiredMessage() },
        );

        // the duration is plain minutes, so the numeric validators apply unchanged
        required(path.durationStepperValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        min(path.durationStepperValue, this.signalFormMinDuration, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });
        max(path.durationStepperValue, this.signalFormMaxDuration, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });

        // the length validators work on collections, so "at least one, at most three
        // selections" needs no hand-written rule — and both reach the dropdown through
        // the `FormUiControl` contract. `required` is avoided on purpose: whether an
        // empty array counts as missing is exactly the ambiguity `minLength` settles.
        minLength(path.dropdownValue, 1, {
            message: <string>this.translateService.instant('ERROR_SELECTION_INVALID'),
        });
        maxLength(path.dropdownValue, this.signalFormMaxSelections, {
            message: <string>this.translateService.instant('ERROR_SELECTION_INVALID'),
        });

        // a radio group is scalar, so the empty string is unambiguously "nothing chosen"
        // and plain `required` says what it means
        required(path.radioValue, { message: <string>this.translateService.instant('ERROR_SELECTION_INVALID') });

        // the rating's `max` rule doubles as the star count, so ten stars are drawn and
        // validated against without saying "ten" twice
        required(path.ratingValue, { message: <string>this.translateService.instant('ERROR_REQUIRED') });
        min(path.ratingValue, 1, { message: <string>this.translateService.instant('ERROR_INVALID') });
        max(path.ratingValue, this.signalFormMaxRating, {
            message: <string>this.translateService.instant('ERROR_INVALID'),
        });

        // the tree's value is a flat collection of leaves, so the same length rules as the
        // dropdown apply and reach the field through the contract
        minLength(path.treeValue, 1, {
            message: <string>this.translateService.instant('ERROR_SELECTION_INVALID'),
        });
        maxLength(path.treeValue, this.signalFormMaxTreeSelections, {
            message: <string>this.translateService.instant('ERROR_SELECTION_INVALID'),
        });
    });

    public readonly signalFormStatus = computed(() => {
        const state = this.signalForm();
        return { dirty: state.dirty(), touched: state.touched(), valid: state.valid() };
    });

    protected readonly textFieldScanFn = this.scanFnFactory.textInput();
    protected readonly numberFieldScanFn = this.scanFnFactory.numberInput();
    protected readonly dropdownFieldScanFn = this.scanFnFactory.dropdownInput();

    protected onDropdownScanNoMatch(processed: string): void {
        this.scanFnFactory.notifyDropdownNoMatch(processed);
    }

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        @Inject(InitializationInstanceToken) private readonly initializationService: BaseInitializationService,
        private readonly alert: PCHAlertService,
    ) {
        this.textInputTest = new TextModel(
            'TextInputTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                pattern: 'Initial.*',
            }),
        );
        this.textInputScannableTest = new TextModel(
            'TextInputScannableTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                scannable: true,
            }),
        );
        this.textInputUnitTest = new TextModel(
            'TextInputUnitTest',
            'Initial',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                pattern: 'Initial.*',
                scannable: true,
                unit: 'MHz',
            }),
        );
        this.textInputReadonlyTest = new TextModel(
            'TextInputReadonlyTest',
            'someValue',
            new TextModelOptions({
                minLength: 5,
                maxLength: 10,
                required: true,
                readonly: true,
            }),
        );

        const numberInitValue = 42;
        this.numberInputTest = new NumberModel(
            'NumberInputTest',
            undefined,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
            }),
        );

        this.numberInputScannableTest = new NumberModel(
            'NumberInputScannableTest',
            numberInitValue,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
                required: true,
                scannable: true,
                unit: 'KW',
            }),
        );

        this.ratingInputTest = new RatingModel(
            'RatingInputTest',
            '',
            new RatingModelOptions({ maxRatings: 10, required: true }),
        );

        this.numberStepperTest = new NumberModel(
            'NumberStepperTest',
            numberInitValue,
            new NumberModelOptions({
                minValue: 0,
                maxValue: 1000,
                step: 5,
            }),
        );

        this.durationStepperTest = new DurationModel(
            'DurationStepperTest',
            moment.duration(),
            new DurationModelOptions({}),
        );

        this.durationStepperStepTest = new DurationModel(
            'DurationStepperTest',
            moment.duration(),
            new DurationModelOptions({
                step: 5,
            }),
        );

        this.durationStepperUndefinedTest = new DurationModel('DurationStepperTest', undefined);

        this.durationStepperMaxValueTest = new DurationModel(
            'DurationMaxValueStepperTest',
            moment.duration('00:10'),
            new DurationModelOptions({
                minValue: moment.duration('00:05'),
                maxValue: moment.duration('01:30'),
            }),
        );

        this.dateInputTest = new DateModel(
            'DateInputTest',
            moment(),
            new DateModelOptions({
                minValue: moment('2024-01-01'),
                maxValue: moment('2025-12-31'),
            }),
        );
        this.timeInputTest = new TimeModel(
            'TimeInputTest',
            moment(),
            new TimeModelOptions({
                minValue: moment().startOf('hour'),
                maxValue: moment().endOf('day'),
            }),
        );

        this.dateTimeInputTest = new TimeModel(
            'DateTimeInputTest',
            moment(),
            new TimeModelOptions({
                minValue: moment('2024-01-01').startOf('hour'),
                maxValue: moment('2025-12-31').endOf('day'),
            }),
        );

        const dropdownOptions = [
            new DisplayDecorator<TextModel>('First entry', new TextModel('FirstEntry', 'first')),
            new DisplayDecorator<TextModel>('Second entry', new TextModel('SecondEntry', 'second')),
            new DisplayDecorator<TextModel>('Third entry', new TextModel('ThirdEntry', 'third')),
            new DisplayDecorator<TextModel>('Fourth entry', new TextModel('FourthEntry', 'fourth')),
        ];
        this.dropdownTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                required: true,
                maxSelections: 1,
                scannable: true,
            }),
        );
        this.dropdownTest.selectByIndex(0);
        this.dropdownInputTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                required: true,
                maxSelections: 1,
                scannable: true,
                displayText: true,
            }),
        );
        this.dropdownDisabledTest = new DropdownModel<TextModel>(
            'DropdownDisabledTest',
            dropdownOptions,
            new DropdownModelOptions({
                maxSelections: 1,
                disabled: true,
                scannable: true,
            }),
        );
        this.dropdownDisabledTest.selectByIndex(0);
        this.dropdownMultiTest = new DropdownModel<TextModel>(
            'DropdownTest',
            dropdownOptions,
            new DropdownModelOptions({
                scannable: false,
                required: true,
                maxSelections: 3,
            }),
        );
        this.dropdownMultiTest.selectByIndex(0);
        const radioOptions = [
            new DisplayDecorator<TextModel>('first', new TextModel('FirstRadio', 'firstRadio')),
            new DisplayDecorator<TextModel>('second', new TextModel('SecondRadio', 'secondRadio')),
            new DisplayDecorator<TextModel>('invalid', new TextModel('InvalidRadio', 'invalidRadio')),
        ];

        this.radioInputTest = new RadioModel<TextModel>(
            'RadioInputTest',
            radioOptions,
            new RadioModelOptions({
                required: true,
                initialSelection: 0,
                invalidSelection: 2,
            }),
        );
        this.radioInputTest.selectByIndex(0);

        this.checkboxInputTest = new DisplayDecorator<BooleanModel>(
            'Checkbox selected?',
            new BooleanModel(
                'BooleanInputTest',
                true,
                new BooleanModelOptions({
                    required: true,
                    showLabel: true,
                }),
            ),
        );

        this.checkboxInputToggleTest = new DisplayDecorator<BooleanModel>(
            'Enable/Disable inputs?',
            new BooleanModel(
                'ToggleInputTest',
                true,
                new BooleanModelOptions({
                    showLabel: false,
                }),
            ),
        );

        const treeViewRoot = createTreeViewData();
        this.treeViewTest = new TreeViewModel<TextModel>(
            'TreeViewTest',
            treeViewRoot,
            new TreeViewModelOptions({
                required: true,
                maxSelections: 2,
            }),
        );
        this.treeViewTest.selectEntryByIdAndTitle('5', 'Child 5');
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.widgetForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
    }

    public ngOnDestroy(): void {
        if (typeof this.appReinitSubscription !== 'undefined') {
            this.appReinitSubscription.unsubscribe();
        }
        if (typeof this.serviceReinitSubscription !== 'undefined') {
            this.serviceReinitSubscription.unsubscribe();
        }
    }

    public showOutmodel(model: ODataModel): void {
        this.modalService.show(OutmodelComponent, {
            backdrop: 'static',
            class: 'modal-lg',
            initialState: {
                model: model,
                label: model.constructor.name || 'ODataModel',
            },
            ariaLabelledBy: 'out-modal-title',
        });
    }

    public startInitApp(): void {
        this.appReinitializationRunning = true;

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
            ariaLabelledBy: 'initialization-progress-modal-title',
        });

        this.appReinitSubscription = modalInstance.content?.rendered$
            .pipe(mergeMap(() => this.initializationService.init()))
            .subscribe(() => {
                this.serviceReinitializationRunning = false;
            });
    }

    public startInitServices(): void {
        this.serviceReinitializationRunning = true;

        const modalInstance = this.modalService.show(InitializationProgressComponent, {
            backdrop: 'static',
            class: 'modal-md modal-dialog-centered',
            initialState: {
                progress$: this.initializationService.initMessages$,
            },
            ariaLabelledBy: 'initialization-progress-modal-title',
        });

        this.serviceReinitSubscription = modalInstance.content?.rendered$
            .pipe(mergeMap(() => this.initializationService.initServices()))
            .subscribe(() => {
                this.serviceReinitializationRunning = false;
            });
    }

    public onCheckboxInputToggleChange(): void {
        this.checkboxInputTest = assignModelOptions(this.checkboxInputTest, {
            ...this.checkboxInputTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.dateInputTest = assignModelOptions(this.dateInputTest, {
            ...this.dateInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.dropdownTest = assignModelOptions(this.dropdownTest, {
            ...this.dropdownTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.dropdownInputTest = assignModelOptions(this.dropdownInputTest, {
            ...this.dropdownInputTest.options,
            disabled: !this.checkboxInputToggleTest.value,
        });
        this.numberInputTest = assignModelOptions(this.numberInputTest, {
            ...this.numberInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.numberInputScannableTest = assignModelOptions(this.numberInputScannableTest, {
            ...this.numberInputScannableTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.numberStepperTest = assignModelOptions(this.numberStepperTest, {
            ...this.numberStepperTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        this.radioInputTest = assignModelOptions(this.radioInputTest, {
            ...this.radioInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
        const newTextInputTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(this.textInputTest.name, this.textInputTest.value, newTextInputTestOptions);
        const newTextInputScannableTestOptions: TextModelOptions = {
            ...this.textInputScannableTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputScannableTest = new TextModel(
            this.textInputScannableTest.name,
            this.textInputScannableTest.value,
            newTextInputScannableTestOptions,
        );
        const newTextInputReadonlyTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(
            this.textInputReadonlyTest.name,
            this.textInputReadonlyTest.value,
            newTextInputReadonlyTestOptions,
        );
        const newTextInputUnitTestOptions: TextModelOptions = {
            ...this.textInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        };
        this.textInputTest = new TextModel(
            this.textInputUnitTest.name,
            this.textInputUnitTest.value,
            newTextInputUnitTestOptions,
        );
        this.timeInputTest = assignModelOptions(this.timeInputTest, {
            ...this.timeInputTest.options,
            readonly: !this.checkboxInputToggleTest.value,
        });
    }

    public onTextInputChange(event: ODataModelChangeEvent<TextModel>): void {
        this.log.debug(`Text input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onNumberInputChange(event: ODataModelChangeEvent<NumberModel>): void {
        this.log.debug(`Number input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onNumberStepperChange(event: ODataModelChangeEvent<NumberModel>): void {
        this.log.debug(`Number stepper input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDurationStepperChange(event: ODataModelChangeEvent<DurationModel>): void {
        this.log.debug(`Duration stepper input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDateInputChange(event: ODataModelChangeEvent<DateModel>): void {
        this.log.debug(`Date input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onTimeInputChange(event: ODataModelChangeEvent<TimeModel>): void {
        this.log.debug(`Time input content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDateTimeInputChange(event: ODataModelChangeEvent<DateModel>): void {
        this.log.debug(`Date-time content changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onDropdownChange(event: ODataModelChangeEvent<DropdownModel<TextModel>>): void {
        this.log.debug(`Dropdown selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onRadioInputChange(event: ODataModelChangeEvent<RadioModel<TextModel>>): void {
        this.log.debug(`Radio input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onCheckboxInputChange(event: ODataModelChangeEvent<BooleanModel>): void {
        this.log.debug(`Checkbox input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    public onTreeviewInputChange(): void {
        this.log.debug(`TreeView selection changed (no info on selected not available)`);
    }

    public onRatingInputChange(event: ODataModelChangeEvent<RatingModel>): void {
        this.log.debug(`Rating input selection changed from '${event.oldValue}' to '${event.newValue}'`);
    }

    /**
     * Commits the whole validated draft in a single intent action. `submit` marks
     * every field touched and skips the action while the form is invalid, so the
     * store never sees a value the schema rejects.
     *
     * @returns whether the form was valid and the commit therefore ran
     */
    public commitSignalForm(): Promise<boolean> {
        return submit(this.signalForm, () => {
            this.store.dispatch(widgetsFormCommitted({ form: this.signalFormDraft() }));
            return Promise.resolve(undefined);
        });
    }

    /**
     * @param event the native submit event, suppressed to keep the browser from navigating
     */
    public onSignalFormSubmit(event: Event): void {
        event.preventDefault();

        this.commitSignalForm().catch((error: Error) =>
            this.log.error('Error committing the widgets signal form: ', error.message),
        );
    }

    /**
     * Writes the current date and time into the slice without going through the
     * form. Stands in for a store update the form did not originate (a rehydrate,
     * an offline sync, another component) so the draft re-seeding can be observed.
     */
    public seedSignalFormFromStore(): void {
        const now = moment();
        this.store.dispatch(
            widgetsFormSeeded({
                form: {
                    dateValue: now.format(HTML5_FMT.DATE),
                    timeValue: now.format(HTML5_FMT.TIME),
                    textValue: 'Initial',
                    multilineTextValue: 'Seeded from outside the form',
                    numberValue: 42,
                    numberStepperValue: 25,
                    checkboxValue: true,
                    durationStepperValue: 30,
                    dropdownValue: ['second'],
                    radioValue: 'second',
                    ratingValue: 7,
                    treeValue: ['child-5'],
                },
            }),
        );
    }

    public resetSignalForm(): void {
        this.store.dispatch(widgetsFormReset());
    }

    /**
     * Range rule for the signal form's date field. An empty value is left to the
     * `required` validator so the two do not report at the same time.
     *
     * @param value the edited HTML5 date string
     * @returns the range error, or undefined when the date is within bounds
     */
    private checkSignalFormDateRange(value: string): ValidationError.WithoutFieldTree | undefined {
        if (!value) {
            return undefined;
        }

        const rangeText = <string>this.translateService.instant('DATE_INPUT_SELECT_DATE_RANGE_TEXT');
        const selected = moment(value, HTML5_FMT.DATE, true);
        if (!selected.isValid()) {
            return { kind: 'invalidDate', message: rangeText };
        }

        if (selected.isBefore(this.signalFormMinDate, 'day')) {
            const fromText = <string>this.translateService.instant('DATE_INPUT_VALID_DATE_RANGE_FROM_TEXT', {
                minDate: moment(this.signalFormMinDate).format(signalFormDateDisplayFormat),
            });
            return { kind: 'minValue', message: `${rangeText}${fromText}` };
        }

        if (selected.isAfter(this.signalFormMaxDate, 'day')) {
            const uptoText = <string>this.translateService.instant('DATE_INPUT_VALID_DATE_RANGE_UPTO_TEXT', {
                maxDate: moment(this.signalFormMaxDate).format(signalFormDateDisplayFormat),
            });
            return { kind: 'maxValue', message: `${rangeText}${uptoText}` };
        }

        return undefined;
    }

    /**
     * Line-count rule for the signal form's multiline text field, the equivalent of
     * the reactive-forms `maxLines` option.
     *
     * @param value the edited text
     * @returns the line-count error, or undefined when the text fits
     */
    private checkSignalFormLineCount(value: string): ValidationError.WithoutFieldTree | undefined {
        if (value.split('\n').length <= this.signalFormMultilineTextMaxLines) {
            return undefined;
        }
        return {
            kind: 'maxLines',
            message: <string>this.translateService.instant('ERROR_MAX_LINES_EXCEEDED'),
        };
    }

    /**
     * Range rule for the signal form's time field. An empty value is left to the
     * `required` validator so the two do not report at the same time.
     *
     * @param value the edited HTML5 time string
     * @returns the range error, or undefined when the time is within bounds
     */
    private checkSignalFormTimeRange(value: string): ValidationError.WithoutFieldTree | undefined {
        if (!value) {
            return undefined;
        }

        const rangeText = <string>this.translateService.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT');
        const selected = moment(value, signalFormTimeParseFormats, true);
        if (!selected.isValid()) {
            return { kind: 'invalidTime', message: rangeText };
        }

        if (selected.isBefore(moment(this.signalFormMinTime, signalFormTimeParseFormats, true))) {
            const fromText = <string>this.translateService.instant('TIME_INPUT_VALID_TIME_RANGE_FROM_TEXT', {
                minTime: this.signalFormMinTime,
            });
            return { kind: 'minValue', message: `${rangeText}${fromText}` };
        }

        if (selected.isAfter(moment(this.signalFormMaxTime, signalFormTimeParseFormats, true))) {
            const uptoText = <string>this.translateService.instant('TIME_INPUT_VALID_TIME_RANGE_UPTO_TEXT', {
                maxTime: this.signalFormMaxTime,
            });
            return { kind: 'maxValue', message: `${rangeText}${uptoText}` };
        }

        return undefined;
    }

    public addAlert(): void {
        const at = new Date(Date.now() + this.alertTimeInSeconds * 1000);
        const alert: NotificationIdentifier = {
            frontId: 'whocares',
            qmNum: getUuidV4(),
            maNum: getUuidV4(),
        };
        this.alert.addReminder(at, alert).pipe(take(1)).subscribe();
    }
}
